import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import ScoreSubmissionDialog from './ScoreSubmissionDialog';
import Phaser from '../phaser-setup';
import { Heart, Trophy, Star } from 'lucide-react';
import LandingPage from './LandingPage';
import correctSound from '../assets/sounds/correct.mp3';
import wrongSound from '../assets/sounds/wrong.mp3';
import gameoverSound from '../assets/sounds/gameover.mp3';
import shootSound from '../assets/sounds/shoot.mp3';
// import bgm1 from '../assets/sounds/bgm1.mp3';  // Commented out bgm1 import
import bgm2 from '../assets/sounds/bgm2.mp3';
import starshipImg from '../assets/images/starship.png';
import backgroundImg2 from '../assets/images/background.png';
import backgroundImg from '../assets/images/space-background.png';
import alienImg from '../assets/images/alien.png';
import './GrammarGame.css';

// 2. Define Phaser Game Reference Outside the Component
const phaserGameRef = { current: null };

// Questions Array (Ensure all questions have unique options)
const fetchQuestions = async (sheetName = null) => {
  const baseUrl =
    'https://script.google.com/macros/s/AKfycbzw5-Sl0MjY7K1MHAq9yeKGEYrmNUCk4aUeBCXhwZpgDGschK992PccA_eJkT_ZODMZWw/exec'; // Replace with your deployed Apps Script URL
  const url = sheetName ? `${baseUrl}?sheet=${sheetName}` : baseUrl;

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (data.status === 'error') {
      throw new Error(data.message);
    }

    // Return the entire response data
    return {
      questions: data.questions || [],
      scores: data.scores || [],
    };
  } catch (error) {
    console.error('Error fetching questions:', error);
    throw error;
  }
};

// Word Colors Array (Ensure all color styles are included)
const WORD_COLORS = [
  {
    background: 'linear-gradient(135deg, #8B0000 0%, #660000 100%)',  // Dark red
    boxShadow: '0 0 15px rgba(139, 0, 0, 0.5)',
  },
  {
    background: 'linear-gradient(135deg, #006666 0%, #004C4C 100%)',  // Dark teal
    boxShadow: '0 0 15px rgba(0, 102, 102, 0.5)',
  },
  {
    background: 'linear-gradient(135deg, #8B6914 0%, #664E10 100%)',  // Dark gold
    boxShadow: '0 0 15px rgba(139, 105, 20, 0.5)',
  },
  {
    background: 'linear-gradient(135deg, #4B0082 0%, #360060 100%)',  // Dark purple
    boxShadow: '0 0 15px rgba(75, 0, 130, 0.5)',
  },
  {
    background: 'linear-gradient(135deg, #00008B 0%, #000066 100%)',  // Dark blue
    boxShadow: '0 0 15px rgba(0, 0, 139, 0.5)',
  },
  {
    background: 'linear-gradient(135deg, #8B3626 0%, #66281C 100%)',  // Dark coral
    boxShadow: '0 0 15px rgba(139, 54, 38, 0.5)',
  },
];

// GameScene Class Definition

class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
    this.questions = []; // Initialize empty questions array
    this.usedQuestions = []; // Track used questions
    this.questionResults = []; // Track results for each question
    this.mistakenQuestions = []; // Track questions answered incorrectly
    this.baseWordSpeed = 50; // Base speed for word falling (will be increased by level)
  }

  getDistanceToOtherWords(x, existingPositions) {
    return existingPositions
      .map((pos) => Math.abs(x - pos))
      .reduce((min, curr) => Math.min(min, curr), Infinity);
  }

  // To be added
  getValidStartPosition(minDistance, existingPositions, padding) {
    const maxAttempts = 50;
    let attempts = 0;

    while (attempts < maxAttempts) {
      const randomX = Phaser.Math.Between(
        padding,
        this.game.config.width - padding
      );
      if (
        existingPositions.length === 0 ||
        this.getDistanceToOtherWords(randomX, existingPositions) >= minDistance
      ) {
        return randomX;
      }
      attempts++;
    }

    // If we couldn't find an ideal position, return the best possible one
    let bestPosition = padding;
    let maxDistance = 0;

    for (
      let x = padding;
      x <= this.game.config.width - padding;
      x += minDistance / 2
    ) {
      const distance = this.getDistanceToOtherWords(x, existingPositions);
      if (distance > maxDistance) {
        maxDistance = distance;
        bestPosition = x;
      }
    }

    return bestPosition;
  }

  init(data) {
    // Receive the callback and questions from React
    this.onSceneReady = data.onSceneReady;
    this.questions = data.questions || []; // Store questions from data
    this.backgrounds = [];
    this.backgroundSpeed = -2;
    
    // Reset tracking arrays
    this.usedQuestions = [];
    this.questionResults = [];
    console.log('Game initialized with empty arrays:', {
      usedQuestions: this.usedQuestions,
      questionResults: this.questionResults
    });
  }

  preload() {
    // Load assets
    this.load.image('starship', starshipImg);
    this.load.image('background', backgroundImg2);
    this.load.image('enemy', alienImg);
    this.load.audio('shoot', shootSound);
    this.load.audio('correct', correctSound);
    this.load.audio('wrong', wrongSound);
    this.load.audio('gameover', gameoverSound);
    // this.load.audio('bgm1', bgm1);  // Commented out bgm1 loading
    this.load.audio('bgm2', bgm2);
  }

  create() {
    if (this.onSceneReady) {
      this.onSceneReady(this);
    }

    const { width, height } = this.game.config;
    const centerX = width / 2;
    const centerY = height / 2;
    this.isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;
    // Background
    this.backgrounds[0] = this.add.tileSprite(
      centerX,
      centerY,
      width,
      height,
      'background'
    );
    this.backgrounds[1] = this.add.tileSprite(
      centerX,
      -centerY,
      width,
      height,
      'background'
    );

    // Spaceship
    this.spaceship = this.physics.add
      .sprite(centerX, height - 50, 'starship')
      .setCollideWorldBounds(true);
    this.spaceship.setScale(0.15);

    // Inputs
    this.cursors = this.input.keyboard.createCursorKeys();
    this.spacebar = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.SPACE
    );
    // Add these lines for touch controls
    if (this.isTouchDevice) {
      let touchStartTime = 0;
      let touchStartX = 0;
      let isTouchMoving = false;

      this.input.on('pointerdown', (pointer) => {
        touchStartTime = this.time.now;
        touchStartX = pointer.x;
        isTouchMoving = false;
      });

      this.input.on('pointermove', (pointer) => {
        if (pointer.isDown) {
          // Consider it a drag if moved more than 10 pixels
          if (Math.abs(pointer.x - touchStartX) > 10) {
            isTouchMoving = true;
            const newX = Phaser.Math.Clamp(
              pointer.x,
              (this.spaceship.width * 0.15) / 2,
              width - (this.spaceship.width * 0.15) / 2
            );
            this.spaceship.x = newX;
            this.spaceship.setVelocityX(0);
          }
        }
      });

      this.input.on('pointerup', (pointer) => {
        const touchDuration = this.time.now - touchStartTime;
        // Shoot only if it was a quick tap (less than 200ms) and didn't move
        if (touchDuration < 200 && !isTouchMoving) {
          this.shootBullet();
        }
      });
    }
    // Groups
    this.bullets = this.physics.add.group({
      classType: Phaser.GameObjects.Text,
      runChildUpdate: true,
    });
    this.words = this.physics.add.group({
      active: true,
      maxSize: -1,
      runChildUpdate: true,
      dragX: 0,
      dragY: 0,
    });
    // Enemy groups
    this.enemies = this.physics.add.group();
    this.enemyBullets = this.physics.add.group({
      classType: Phaser.GameObjects.Text,
      runChildUpdate: true,
    });
    // Sounds
    this.shootSound = this.sound.add('shoot');
    this.correctSound = this.sound.add('correct');
    this.wrongSound = this.sound.add('wrong');
    this.gameoverSound = this.sound.add('gameover');
    // this.bgm1 = this.sound.add('bgm1', { loop: true, volume: 0.01 });  // Commented out bgm1
    this.bgm2 = this.sound.add('bgm2', { loop: true, volume: 0.3 });

    // Score and Lives
    this.score = 0;
    this.lives = 3;
    this.level = 1;
    this.combo = 0;

    // HUD Updates via Events
    this.events.emit('updateScore', this.score);
    this.events.emit('updateLives', this.lives);
    this.events.emit('updateLevel', this.level);
    this.events.emit('updateCombo', this.combo);

    // Spawn the first question
    this.spawnQuestion();

    // Collision Detection
    this.physics.add.overlap(
      this.bullets,
      this.words,
      this.handleCollision,
      null,
      this
    );

    // Enemy collision detection
    this.physics.add.overlap(
      this.enemyBullets,
      this.spaceship,
      this.handleEnemyBulletCollision,
      null,
      this
    );

    // Player bullet to enemy collision
    this.physics.add.overlap(
      this.bullets,
      this.enemies,
      this.handleEnemyHit,
      null,
      this
    );

    // Enemy spawning timer
    this.time.addEvent({
      delay: 5000, // Spawn enemy every 5 seconds
      callback: this.spawnEnemy,
      callbackScope: this,
      loop: true,
    });

    // Game Over Flag
    this.isGameOver = false;
    this.waitingForNextQuestion = false; // Flag to manage question flow
    
    // Start background music
    if (this.bgm2) {
      this.bgm2.play();
    }
  }

  update() {
    this.backgrounds.forEach((bg) => {
      bg.tilePositionY += this.backgroundSpeed;
    });

    // Handle enemy shooting
    const currentTime = this.time.now;
    this.enemies.getChildren().forEach((enemy) => {
      // Bounce off screen edges
      if (enemy.x <= 50) {
        enemy.x = 50;
        enemy.body.velocity.x = Math.abs(enemy.body.velocity.x);
      } else if (enemy.x >= this.game.config.width - 50) {
        enemy.x = this.game.config.width - 50;
        enemy.body.velocity.x = -Math.abs(enemy.body.velocity.x);
      }

      // Check if it's time to shoot
      if (currentTime - enemy.lastShot >= enemy.shootDelay) {
        this.enemyShoot(enemy);
        enemy.lastShot = currentTime;
        enemy.shootDelay = Phaser.Math.Between(2000, 4000); // Reset shoot delay
      }
    });

    if (!this.input.activePointer.isDown || !this.isTouchDevice) {
      if (this.cursors.left.isDown) {
        this.spaceship.setVelocityX(-300);
      } else if (this.cursors.right.isDown) {
        this.spaceship.setVelocityX(300);
      } else {
        this.spaceship.setVelocityX(0);
      }
    }

    if (Phaser.Input.Keyboard.JustDown(this.spacebar)) {
      this.shootBullet();
    }

    if (this.waitingForNextQuestion || this.isGameOver) return;

    // Remove words that have fallen off the screen
    this.words.getChildren().forEach((word) => {
      if (word && word.y > this.game.config.height - 10 && word.body.enable) {
        // Only check enabled bodies
        const isCorrect = word.getData('isCorrect');
        if (isCorrect) {
          this.lives = 0;
          this.events.emit('updateLives', this.lives);
          this.bgm2.stop(); // Stop gameplay music
          this.gameoverSound.play();

          // Wait for the sound to finish before cleanup
          this.time.delayedCall(1000, () => {
            this.events.emit('gameOver', this.score);
            this.physics.pause();
            this.isGameOver = true;
            // bgm1 is commented out for now
          });
        }
        word.destroy();
      }
    });
    // Update boundary checks for words
    this.words.getChildren().forEach((word) => {
      const padding = 20;
      if (word.x <= padding) {
        word.x = padding;
        word.body.velocity.x = Math.abs(word.body.velocity.x);
      } else if (word.x >= this.game.config.width - padding) {
        word.x = this.game.config.width - padding;
        word.body.velocity.x = -Math.abs(word.body.velocity.x);
      }
    });
    // Remove bullets that have gone off-screen
    this.bullets.getChildren().forEach((bullet) => {
      if (bullet.y < 0) {
        bullet.destroy();
      }
    });

    // Rotate words for dynamic effect
    this.words.getChildren().forEach((word) => {
      if (word.rotationSpeed) {
        word.rotation += word.rotationSpeed;
      }
    });

    this.words.getChildren().forEach((word) => {
      // Bounce off left and right edges manually with padding
      const padding = 20; // Add some padding to prevent sticking
      if (word.x <= padding) {
        word.x = padding; // Prevent sticking to edge
        word.body.velocity.x = Math.abs(word.body.velocity.x); // Force positive velocity
      } else if (word.x >= 1000 - padding) {
        word.x = 1000 - padding; // Prevent sticking to edge
        word.body.velocity.x = -Math.abs(word.body.velocity.x); // Force negative velocity
      }

      // Maintain minimum speeds
      // Store initial velocities if not already stored
      if (!word.getData('initialVelocityX')) {
        word.setData('initialVelocityX', word.body.velocity.x);
        word.setData('initialVelocityY', word.body.velocity.y);
      }

      // Use stored initial velocities as minimum speeds for each word
      const minSpeedX = Math.abs(word.getData('initialVelocityX'));
      const minSpeedY = word.getData('initialVelocityY');

      if (Math.abs(word.body.velocity.x) < minSpeedX) {
        const direction = word.body.velocity.x >= 0 ? 1 : -1;
        word.body.setVelocityX(minSpeedX * direction);
      }
      if (word.body.velocity.y < minSpeedY) {
        word.body.setVelocityY(minSpeedY);
      }
    });
  }

  shootBullet() {
    // Create a bullet as a Phaser text object with thicker appearance
    const bullet = this.bullets.create(
      this.spaceship.x,
      this.spaceship.y - 40,
      '║',  // Using thicker Unicode character
      {
        font: '24px Arial',
        fill: '#ffa500',
        stroke: '#ff8c00',     // Dark orange stroke
        strokeThickness: 4,    // Thick stroke
        shadow: {
          offsetX: 0,
          offsetY: 0,
          color: '#ffd700',    // Golden glow
          blur: 10,
          stroke: true,
          fill: true
        }
      }
    );
    bullet.setOrigin(0.5, 0.5);
    bullet.setDepth(1);
    
    // Add a glow effect using a particle emitter
    const particles = this.add.particles(bullet.x, bullet.y, {
      follow: bullet,
      frequency: 50,
      scale: { start: 0.4, end: 0 },
      alpha: { start: 0.6, end: 0 },
      tint: 0xffd700,
      blendMode: 'ADD',
      lifespan: 200
    });

    // Enable physics for the bullet
    this.physics.world.enable(bullet);
    bullet.body.setAllowGravity(false);
    bullet.body.setVelocityY(-500);

    // Store particle reference to destroy it later
    bullet.particles = particles;

    // Add update listener to destroy particles when bullet is destroyed
    bullet.on('destroy', () => {
      if (bullet.particles) {
        bullet.particles.destroy();
      }
    });

    this.shootSound.play();
  }

  spawnQuestion() {
    if (this.isGameOver || this.waitingForNextQuestion) return;
    
    // Get unused questions
    const unusedQuestions = this.questions.filter(q => !this.usedQuestions.includes(q.question));
    
    // If all questions have been used, move to next level
    if (unusedQuestions.length === 0) {
      // Go to next level
      this.level += 1;
      this.events.emit('updateLevel', this.level);
      
      // Reset used questions array for the new level
      this.usedQuestions = [];
      
      // Create level up text
      const levelUpText = this.add.text(
        this.game.config.width / 2,
        this.game.config.height / 2,
        `レベル ${this.level}！`,
        {
          font: 'bold 64px Arial',
          fill: '#FFD700',
          stroke: '#FF8C00',
          strokeThickness: 8,
          shadow: { color: '#FF4500', blur: 10, stroke: true, fill: true }
        }
      ).setOrigin(0.5);
      
      // Add glow effect
      const glowFX = this.add.particles(levelUpText.x, levelUpText.y, {
        lifespan: 1000,
        gravityY: 0,
        scale: { start: 0.5, end: 0 },
        alpha: { start: 0.5, end: 0 },
        blendMode: 'ADD',
        tint: 0xFFD700,
        quantity: 2,
        frequency: 50,
        angle: { min: 0, max: 360 },
        speed: { min: 50, max: 100 },
        emitting: true
      });

      // Create stars for additional effect
      for (let i = 0; i < 20; i++) {
        const angle = (i / 20) * Math.PI * 2;
        const distance = 100;
        const star = this.add.star(
          levelUpText.x + Math.cos(angle) * distance,
          levelUpText.y + Math.sin(angle) * distance,
          5, // 5 points star
          5, // inner radius
          20, // outer radius
          0xFFD700 // gold color
        ).setAlpha(0);

        // Animate each star
        this.tweens.add({
          targets: star,
          alpha: { from: 0, to: 1 },
          scale: { from: 0.1, to: 1 },
          angle: 360,
          duration: 500,
          ease: 'Back.out',
          delay: i * 50
        });

        // Cleanup star after animation
        this.time.delayedCall(2000, () => {
          this.tweens.add({
            targets: star,
            alpha: 0,
            scale: 0,
            duration: 300,
            onComplete: () => star.destroy()
          });
        });
      }

      // Add scale and shine animation to level text
      this.tweens.add({
        targets: levelUpText,
        scale: { from: 0.5, to: 1 },
        duration: 500,
        ease: 'Back.out'
      });

      // Add floating animation
      this.tweens.add({
        targets: levelUpText,
        y: levelUpText.y - 20,
        duration: 1000,
        yoyo: true,
        repeat: 1,
        ease: 'Sine.inOut'
      });

      // Add pulsating effect
      this.tweens.add({
        targets: levelUpText,
        scale: { from: 1, to: 1.1 },
        duration: 500,
        yoyo: true,
        repeat: 3,
        ease: 'Sine.inOut'
      });

      // Cleanup after animations
      this.time.delayedCall(2000, () => {
        this.tweens.add({
          targets: [levelUpText],
          alpha: 0,
          scale: 2,
          duration: 500,
          ease: 'Back.in',
          onComplete: () => {
            levelUpText.destroy();
            glowFX.destroy();
          }
        });
      });
      
      // Log level progression
      console.log(`Level up! Starting level ${this.level} with increased speed`);
      console.log('Resetting used questions array for new level');
      
      // Continue with next question rather than ending game
      return this.spawnQuestion();
    }

    
    const fontSize = this.game.config.width <= 768 ? 12 : 20;
    const question = Phaser.Utils.Array.GetRandom(unusedQuestions);
    
    // Add to used questions array
    this.usedQuestions.push(question.question);
    
    // Log the progress
    console.log('Questions used so far:', this.usedQuestions.length, 'out of', this.questions.length);
    console.log('Used Questions Array:', JSON.stringify(this.usedQuestions, null, 2));
    console.log('Remaining questions:', unusedQuestions.length - 1);
    const options = Phaser.Utils.Array.Shuffle(question.options);
    const correctOption = question.correct;

    // Update question text
    this.events.emit('updateQuestion', question.question);

    const existingPositions = [];
    const minDistance = 150; // Minimum pixels between words
    const padding = 100; // Padding from edges

    options.forEach((option, index) => {
      const colorStyle = WORD_COLORS[index % WORD_COLORS.length];
      const isCorrect = option === correctOption;

      const backgroundColorMatch =
        colorStyle.background.match(/#([0-9A-Fa-f]{6})/);
      const backgroundColor = backgroundColorMatch
        ? backgroundColorMatch[0]
        : '#ffffff';

      const fillColor = '#ffffff';

      // Get valid position with minimum distance from other words
      const xPosition = this.getValidStartPosition(
        minDistance,
        existingPositions,
        padding
      );
      existingPositions.push(xPosition);

      // Create Text object with background styling at randomX and y=0
      const text = this.add
        .text(xPosition, 0, option, {
          font: `${fontSize}px Arial`, // Use dynamic fontSize
          fill: fillColor,
          backgroundColor: backgroundColor,
          padding: { x: 8, y: 8 }, // Also slightly reduce padding for mobile
          align: 'center',
        })
        .setOrigin(0.5);

      // Adjust display size to include padding
      text.setDisplaySize(text.width + 20, text.height + 20);

      // Enable physics for the Text object
      this.physics.world.enable(text);
      this.words.add(text);
      text.body.setAllowGravity(false);
      text.body.setCollideWorldBounds(false); // Remove collision with world bounds

      // Calculate level speed multiplier (25% increase per level)
      const levelSpeedMultiplier = 1 + ((this.level - 1) * 0.25);
      
      console.log(`Level ${this.level} - Speed Multiplier: ${levelSpeedMultiplier}`);

      // Base speeds
      const baseHorizontalSpeed = 100;
      const maxHorizontalSpeed = 250;
      const baseVerticalSpeed = 50; // Fixed base vertical speed
      
      // Calculate speeds
      const horizontalSpeed = Phaser.Math.Between(
        baseHorizontalSpeed * levelSpeedMultiplier,
        maxHorizontalSpeed * levelSpeedMultiplier
      );
      const verticalSpeed = baseVerticalSpeed * levelSpeedMultiplier;
      
      console.log(`Speed for Level ${this.level}:
        Horizontal: ${horizontalSpeed}
        Vertical: ${verticalSpeed} (Fixed base speed with level multiplier)`);

      // Direction for horizontal movement

      
      console.log(`Speed for Level ${this.level}:
        Horizontal: ${horizontalSpeed}
        Vertical: ${verticalSpeed} (Fixed base speed with level multiplier)`);


      // Randomly decide direction
      const direction = Phaser.Math.Between(0, 1) ? 1 : -1;

      text.body.setVelocity(horizontalSpeed * direction, verticalSpeed);

      text.body.setSize(text.displayWidth, text.displayHeight);

      // Use Data Manager to set 'isCorrect'
      text.setData('isCorrect', isCorrect);
      console.log(`Word "${option}" is correct: ${text.getData('isCorrect')}`); // Debug log

      // Optional: Add rotation speed for dynamic effect
      text.rotationSpeed = Phaser.Math.FloatBetween(-0.005, 0.005);
    });
  }

  spawnEnemy() {
    if (this.isGameOver) return;

    // Check if there's already an enemy
    if (this.enemies.getChildren().length > 0) return;

    const x = Phaser.Math.Between(50, this.game.config.width - 50);
    const enemy = this.enemies.create(x, 50, 'enemy');
    enemy.setScale(0.15);

    // Random horizontal movement
    const speed = Phaser.Math.Between(50, 100);
    enemy.setVelocityX(speed * (Math.random() < 0.5 ? 1 : -1));

    // Add properties for shooting
    enemy.lastShot = 0;
    enemy.shootDelay = Phaser.Math.Between(2000, 4000); // Random delay between shots

    // Ensure enemy stays within bounds
    enemy.setCollideWorldBounds(true);
    enemy.setBounce(1, 0); // Bounce off walls
  }

  enemyShoot(enemy) {
    if (this.isGameOver) return;

    const bullet = this.enemyBullets.create(enemy.x, enemy.y + 20, '║');
    bullet.setStyle({
      font: '24px Arial',
      fill: '#ff0000',
      stroke: '#cc0000',     // Darker red stroke
      strokeThickness: 4,    // Thick stroke
      shadow: {
        offsetX: 0,
        offsetY: 0,
        color: '#ff3333',    // Light red glow
        blur: 10,
        stroke: true,
        fill: true
      }
    });
    bullet.setOrigin(0.5, 0.5);

    // Add a glow effect using a particle emitter
    const particles = this.add.particles(bullet.x, bullet.y, {
      follow: bullet,
      frequency: 50,
      scale: { start: 0.4, end: 0 },
      alpha: { start: 0.6, end: 0 },
      tint: 0xff3333,
      blendMode: 'ADD',
      lifespan: 200
    });

    // Enable physics for the bullet
    this.physics.world.enable(bullet);
    bullet.body.setAllowGravity(false);

    // Set velocity straight down
    const speed = 300;
    bullet.body.setVelocity(0, speed);

    // Store particle reference to destroy it later
    bullet.particles = particles;

    // Add update listener to destroy particles when bullet is destroyed
    bullet.on('destroy', () => {
      if (bullet.particles) {
        bullet.particles.destroy();
      }
    });
  }

  handleEnemyBulletCollision(spaceship, bullet) {
    if (this.isGameOver) return;

    bullet.destroy();
    this.wrongSound.play();

    // Reduce score and lives
    this.score = Math.max(0, this.score - 50);
    this.combo = 0;
    this.lives = Math.max(0, this.lives - 1);

    // Update HUD
    this.events.emit('updateScore', this.score);
    this.events.emit('updateCombo', this.combo);
    this.events.emit('updateLives', this.lives);

    // Check for game over
    if (this.lives <= 0) {
      this.gameoverSound.play();
      this.time.delayedCall(1000, () => {
        this.events.emit('gameOver', this.score);
        this.physics.pause();
        this.isGameOver = true;
      });
    }
  }

  handleEnemyHit(bullet, enemy) {
    if (this.isGameOver) return;

    // Calculate the center point of the enemy
    const enemyCenterY = enemy.y + (enemy.displayHeight * 0.15) / 2; // Adjusting for the 0.15 scale
    
    // Only trigger the hit when the bullet reaches near the enemy's center
    if (bullet.y > enemy.y - 10 && bullet.y < enemyCenterY) {
      // Disable further collisions
      bullet.body.enable = false;
      enemy.body.enable = false;

      // Create a small flash effect at the impact point
      const flash = this.add.circle(bullet.x, bullet.y, 15, 0xffffff, 1);
      this.tweens.add({
        targets: flash,
        alpha: 0,
        scale: 2,
        duration: 200,
        onComplete: () => flash.destroy()
      });

      // Add a slight delay before destroying for better visual effect
      this.time.delayedCall(50, () => {
        // Play sound and add score
        this.correctSound.play();
        this.score += 50;
        this.events.emit('updateScore', this.score);

        // Add particle effect for enemy destruction
        const particles = this.add.particles(enemy.x, enemy.y, {
          lifespan: 800,
          gravityY: 0,
          scale: { start: 0.4, end: 0 },
          alpha: { start: 1, end: 0 },
          blendMode: 'ADD',
          tint: 0xff0000,
          quantity: 20,
          angle: { min: 0, max: 360 },
          speed: { min: 50, max: 100 },
        });

        // Destroy both the bullet and the enemy
        bullet.destroy();
        enemy.destroy();

        // Clean up particles after animation
        this.time.delayedCall(800, () => {
          particles.destroy();
        });
      });
    }
  }

  handleCollision(bullet, word) {
    // To be added
    // If both objects are words (not bullets), ignore the collision
    if (
      !(bullet instanceof Phaser.GameObjects.Text) ||
      !bullet.body ||
      !bullet.body.gameObject ||
      bullet.body.gameObject.getData('isCorrect') !== undefined
    ) {
      return;
    }

    // Ensure 'word' is a Text object
    if (!(word instanceof Phaser.GameObjects.Text)) {
      console.warn('Collision detected with a non-Text object:', word);
      return;
    }

    // Prevent multiple collisions for the same word
    if (word.isProcessing) return;
    word.isProcessing = true;

    // Retrieve the 'isCorrect' data from the word
    const isCorrect = word.getData('isCorrect');
    console.log(
      `Collision detected with word: "${isCorrect ? 'Correct' : 'Incorrect'}"`
    );
    console.log('Collided with:', word);

    // Destroy the bullet
    bullet.destroy();

    if (isCorrect) {
      // Correct answer logic
      this.correctSound.play();
      this.score += 100;
      this.combo += 1;
      this.events.emit('updateScore', this.score);
      this.events.emit('updateCombo', this.combo);

      // Add combo animation and bonus points if combo is 3 or higher
      if (this.combo >= 3) {
        // Add 50 points bonus for combo
        this.score += 50;
        this.events.emit('updateScore', this.score);
        const comboText = this.add.text(
          this.game.config.width / 2,
          this.game.config.height / 4, // Changed from /2 to /4 to appear in the top quarter of the screen
          `${this.combo} コンボ！`,
          {
            font: 'bold 64px Arial',
            fill: '#FFD700',
            stroke: '#FF8C00',
            strokeThickness: 8,
            shadow: { color: '#FF4500', blur: 10, stroke: true, fill: true }
          }
        ).setOrigin(0.5).setDepth(1000);

        // Add glow particle effect
        const particles = this.add.particles(comboText.x, comboText.y, {
          lifespan: 1000,
          gravityY: -50,
          scale: { start: 0.5, end: 0 },
          alpha: { start: 0.5, end: 0 },
          blendMode: 'ADD',
          tint: 0xFFD700,
          quantity: 2,
          frequency: 50,
          angle: { min: 0, max: 360 },
          speed: { min: 50, max: 100 },
          emitting: true
        });

        // Create stars for additional effect
        for (let i = 0; i < 10; i++) {
          const angle = (i / 10) * Math.PI * 2;
          const distance = 80;
          const star = this.add.star(
            comboText.x + Math.cos(angle) * distance,
            comboText.y + Math.sin(angle) * distance,
            5, // 5 points star
            5, // inner radius
            15, // outer radius
            0xFFD700 // gold color
          ).setAlpha(0).setDepth(1000);

          // Animate each star
          this.tweens.add({
            targets: star,
            alpha: { from: 0, to: 1 },
            scale: { from: 0.1, to: 1 },
            angle: 360,
            duration: 500,
            ease: 'Back.out',
            delay: i * 50
          });

          // Cleanup star after animation
          this.time.delayedCall(1000, () => {
            this.tweens.add({
              targets: star,
              alpha: 0,
              scale: 0,
              duration: 300,
              onComplete: () => star.destroy()
            });
          });
        }

        // Add scale and shine animation
        this.tweens.add({
          targets: comboText,
          scale: { from: 0.5, to: 1 },
          duration: 500,
          ease: 'Back.out'
        });

        // Add floating animation
        this.tweens.add({
          targets: comboText,
          y: comboText.y - 20,
          duration: 1000,
          yoyo: true,
          repeat: 1,
          ease: 'Sine.inOut'
        });

        // Add pulsating effect
        this.tweens.add({
          targets: comboText,
          scale: { from: 1, to: 1.1 },
          duration: 500,
          yoyo: true,
          repeat: 3,
          ease: 'Sine.inOut'
        });

        // Cleanup after animations
        this.time.delayedCall(2000, () => {
          this.tweens.add({
            targets: [comboText],
            alpha: 0,
            scale: 2,
            duration: 500,
            ease: 'Back.in',
            onComplete: () => {
              comboText.destroy();
              particles.destroy();
            }
          });
        });
      }
      
      // Track the result with full question context
      const correctQuestion = this.questions.find(q => q.correct === word.text);
      this.questionResults.push({
        question: correctQuestion.question,
        answer: word.text,
        result: 'correct'
      });
      console.log('Question answered correctly:', correctQuestion.question, 'with answer:', word.text);


      // Set all words to ignore boundaries and remove their physics checks
      this.words.getChildren().forEach((wordObj) => {
        wordObj.body.enable = false; // Disable physics body
        wordObj.setData('isCorrect', null); // Remove isCorrect flag so it won't trigger game over
      });

      // Get the complete sentence
      const question = this.questions.find((q) => q.correct === word.text);
      const completeSentence = question.question.replace('____', word.text);

      // Update the question text to show complete sentence
      this.events.emit('updateQuestion', completeSentence);

      // Scatter all words with enhanced effect
      this.words.getChildren().forEach((wordObj) => {
        const angle = Math.random() * Math.PI * 2;
        const distance = Phaser.Math.Between(600, 1000); // Increased scatter distance
        const targetX = wordObj.x + Math.cos(angle) * distance;
        const targetY = wordObj.y + Math.sin(angle) * distance;

        // Enhanced particle effect
        const particles = this.add.particles(wordObj.x, wordObj.y, {
          lifespan: 800,
          gravityY: 0,
          scale: { start: 0.8, end: 0 },
          alpha: { start: 1, end: 0 },
          blendMode: 'ADD',
          tint: 0xffffff,
          quantity: 3,
          angle: { min: 0, max: 360 },
          speed: { min: 50, max: 100 },
        });

        // More dramatic scatter animation
        this.tweens.add({
          targets: wordObj,
          x: targetX,
          y: targetY,
          alpha: 0,
          scale: 2,
          rotation: angle + Math.PI * 6,
          duration: 1000,
          ease: 'Power2',
          onComplete: () => {
            particles.destroy();
            wordObj.destroy();
          },
        });
      });

      // Create and animate the complete sentence
      const fontSize =
        this.game.config.width <= 480
          ? 24
          : this.game.config.width <= 768
          ? 32
          : 45;

      const completeText = this.add
        .text(
          this.game.config.width / 2,
          this.game.config.height / 2,
          completeSentence,
          {
            font: `${fontSize}px Arial`,
            fill: '#ffffff',
            align: 'center',
            stroke: '#000000',
            strokeThickness: 4,
            wordWrap: { width: this.game.config.width * 0.9 }, // 90% of screen width
            lineSpacing: 10,
          }
        )
        .setOrigin(0.5);

      // Create a glow effect container
      const glowFX = this.add.graphics();
      glowFX.setPosition(500, 325);

      // Entrance animation for complete sentence
      this.tweens.add({
        targets: completeText,
        alpha: 1,
        scale: 1,
        duration: 500,
        ease: 'Back.out',
        onComplete: () => {
          // Pulsing animation
          this.tweens.add({
            targets: completeText,
            scale: 1.1,
            duration: 1000,
            yoyo: true,
            repeat: 1,
            ease: 'Sine.inOut',
          });
        },
      });

      // Rotating glow effect
      let glowAngle = 0;
      const glowTimer = this.time.addEvent({
        delay: 16,
        callback: () => {
          glowFX.clear();
          glowFX.lineStyle(30, 0xffff00, 0.1);
          glowFX.beginPath();
          const radius = 100;
          const x = radius * Math.cos(glowAngle);
          const y = radius * Math.sin(glowAngle);
          glowFX.moveTo(-x, -y);
          glowFX.lineTo(x, y);
          glowAngle += 0.05;
        },
        loop: true,
      });

      // Clean up after animation
      this.time.delayedCall(2000, () => {
        // Clear the question text at the top when starting fade out
        this.events.emit('updateQuestion', '');
        this.tweens.add({
          targets: [completeText, glowFX],
          alpha: 0,
          scale: 2,
          duration: 500,
          ease: 'Back.in',
          onComplete: () => {
            completeText.destroy();
            glowFX.destroy();
            glowTimer.destroy();
            // Add a short delay before spawning next question
            this.time.delayedCall(1000, () => {
              this.waitingForNextQuestion = false;
              this.spawnQuestion();
            });
          },
        });
      });
    } else {
      // Incorrect answer logic
      this.wrongSound.play();
      this.score = Math.max(0, this.score - 50);
      this.combo = 0;
      this.lives = Math.max(0, this.lives - 1);
      
      // Track the result and add to mistaken questions
      const currentQuestion = this.questions.find(q => q.options.includes(word.text));
      if (currentQuestion && !this.mistakenQuestions.includes(currentQuestion.question)) {
        this.mistakenQuestions.push(currentQuestion.question);
        console.log('Added to mistaken questions:', currentQuestion.question);
        console.log('Current mistaken questions:', this.mistakenQuestions);
      }
      
      this.questionResults.push({
        question: currentQuestion.question,
        answer: word.text,
        result: 'incorrect'
      });
      console.log('Question answered incorrectly:', word.text);
      this.events.emit('updateScore', this.score);
      this.events.emit('updateCombo', this.combo);
      this.events.emit('updateLives', this.lives);

if (this.lives <= 0) {
        // Stop background music before playing game over sound
        if (this.bgm2 && this.bgm2.isPlaying) {
          this.bgm2.stop();
        }
        
        this.gameoverSound.play();

        // Wait for the sound to finish before cleanup
        this.time.delayedCall(1000, () => {
          this.events.emit('gameOver', this.score);

          // Clean up the scene
          this.physics.pause();
          this.words.clear(true, true);
          this.bullets.clear(true, true);

          // Stop all physics
          this.physics.world.timeScale = 0;
          this.physics.world.isPaused = true;

          this.isGameOver = true;

          // Stop any remaining sounds
          this.sound.stopAll();
        });
      }

      word.destroy();
    }
  }
}

// Phaser Game Configuration
const createPhaserConfig = (onSceneReadyCallback) => {
  const gameWidth = Math.min(window.innerWidth, 1000);
  const isPortrait = window.innerHeight > window.innerWidth;

  // Calculate heights of UI elements
  const hudElement = document.querySelector('.hud');
  const questionContainer = document.querySelector('.question-container');
  const hudHeight = hudElement ? hudElement.offsetHeight : 0;
  const questionHeight = questionContainer ? questionContainer.offsetHeight : 0;
  const totalMargins = 32; // 2rem for padding

  // For portrait mode, use more vertical space
  let gameHeight;
  if (isPortrait) {
    const availableHeight =
      window.innerHeight - (hudHeight + questionHeight + totalMargins);
    gameHeight = Math.floor(availableHeight); // Increase height by 20% in portrait mode
  } else {
    gameHeight =
      Math.floor(
        window.innerHeight - (hudHeight + questionHeight + totalMargins)
      ) * 0.99;
  }

  return {
    type: Phaser.AUTO,
    width: gameWidth,
    height: gameHeight,
    backgroundColor: '#000000',
    parent: 'phaser-container',
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_HORIZONTALLY,
      width: gameWidth,
      height: gameHeight,
    },
    physics: {
      default: 'arcade',
      arcade: {
        debug: false,
      },
    },
    scene: [GameScene],
  };
};

// GrammarGame Component
const GrammarGame = () => {
  console.log('GrammarGame component rendered');

  // 5.1. State Hooks
  const [questions, setQuestions] = useState(null);
  const [showScoreSubmission, setShowScoreSubmission] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isPortrait: window.innerHeight > window.innerWidth,
  });
  const [isPortrait, setIsPortrait] = useState(
    window.innerHeight > window.innerWidth
  );
  const [showLanding, setShowLanding] = useState(true);
  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [combo, setCombo] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [displayScore, setDisplayScore] = useState(0);
  
  // Effect for animating score counting
  useEffect(() => {
    if (gameOver) {
      const duration = 800; // 0.8 seconds duration
      const steps = 30; // Fewer steps for faster animation
      const stepDuration = duration / steps;
      const increment = score / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= score) {
          setDisplayScore(score);
          clearInterval(timer);
        } else {
          setDisplayScore(Math.floor(current));
        }
      }, stepDuration);
      
      return () => clearInterval(timer);
    } else {
      setDisplayScore(0);
    }
  }, [gameOver, score]);

  // 5.2. Fetch Questions
  // 5.2. Fetch Questions
  const searchParams = new URLSearchParams(window.location.search);
  const sheetName = searchParams.get('id');

  // Add scores state
  const [scores, setScores] = useState([]);
  const [questionsAndScores, setQuestionsAndScores] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        // Load questions and scores
        const response = await fetchQuestions(sheetName);
        console.log('Fetched data:', response); // Debug log

        if (response.questions) {
          setQuestions(response.questions);
        }

        if (response.scores) {
          console.log('Setting scores:', response.scores); // Debug log
          setScores(response.scores);
        }

        setQuestionsAndScores(response);

        // Add a delay to ensure proper initialization
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (err) {
        console.error('Error loading data:', err); // Debug log
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [sheetName]); // Add sheetName as dependency
  // 5.3. Handle Window Resize
  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
        isPortrait: window.innerHeight > window.innerWidth,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 5.4. Memoize Questions to Ensure Stability
  const memoizedQuestions = useMemo(() => questions, [questions]);

  // Track initialization state
  const [isGameReady, setIsGameReady] = useState(false);
  const initTimeoutRef = useRef(null);
  const isInitializingRef = useRef(false);

  useEffect(() => {
    // Prevent running effect if already initialized
    if (
      !gameStarted ||
      !memoizedQuestions ||
      isInitializingRef.current ||
      phaserGameRef.current
    ) {
      return;
    }

    const initGame = () => {
      // Initialize Phaser Game
      const onSceneReady = (scene) => {
        console.log('Phaser scene is ready');
        setIsGameReady(true);
        isInitializingRef.current = false;

        // Remove existing listeners to prevent duplicates
        scene.events.removeAllListeners('updateScore');
        scene.events.removeAllListeners('updateLives');
        scene.events.removeAllListeners('updateLevel');
        scene.events.removeAllListeners('updateCombo');
        scene.events.removeAllListeners('gameOver');

        // Add fresh listeners
        scene.events.on('updateScore', setScore);
        scene.events.on('updateLives', setLives);
        scene.events.on('updateLevel', setLevel);
        scene.events.on('updateCombo', setCombo);
        scene.events.on('updateQuestion', setCurrentQuestion);
        scene.events.on('gameOver', (finalScore) => {
          console.log('Game Over with score:', finalScore);
          setGameOver(true);
          setShowScoreSubmission(true);
        });

        // Ensure the scene is properly started
        if (scene.scene.isActive()) {
          scene.physics.resume();
          scene.spawnQuestion();
        }
      };

      // Create new game instance
      phaserGameRef.current = new Phaser.Game(createPhaserConfig(onSceneReady));
      // Initialize fresh scene with reset arrays
      phaserGameRef.current.scene.start('GameScene', {
        onSceneReady,
        questions: memoizedQuestions,
      });
      
      // Reset arrays in the scene after it's started
      const gameScene = phaserGameRef.current.scene.getScene('GameScene');
      if (gameScene) {
        gameScene.usedQuestions = [];
        gameScene.questionResults = [];
        console.log('Arrays reset. Used Questions:', gameScene.usedQuestions);
      }
    };

    // Main initialization logic
    if (!gameStarted || !memoizedQuestions || isInitializingRef.current) {
      return;
    }

    console.log('Starting game initialization');
    isInitializingRef.current = true;

    // Cleanup existing instance if any
    if (phaserGameRef.current) {
      phaserGameRef.current.scene.scenes.forEach((scene) => {
        scene.sound?.stopAll();
        scene.events?.removeAllListeners();
      });
      phaserGameRef.current.destroy(true);
      phaserGameRef.current = null;
    }

    // Clear any existing timeout
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
    }

    // Add delay before initialization
    // Initialize game immediately
    initGame();

    // Cleanup function
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }

      if (phaserGameRef.current) {
        phaserGameRef.current.scene.scenes.forEach((scene) => {
          scene.sound?.stopAll();
          scene.events?.removeAllListeners();
        });
        phaserGameRef.current.destroy(true);
        phaserGameRef.current = null;
      }

      setIsGameReady(false);
      isInitializingRef.current = false;
    };
  }, [gameStarted, memoizedQuestions]);

  // 5.6. Reset Game Function
  const resetGame = () => {
    setScore(0);
    setLives(3);
    setLevel(1);
    setCombo(0);
    setGameOver(false);
    setGameStarted(false);
    setShowLanding(true);
    setShowScoreSubmission(false);
  };

  // 5.7. Render JSX
  return (
    <div
      className="relative w-full min-h-screen flex flex-col items-center justify-center p-4"
      style={{
        backgroundImage: `url(${backgroundImg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      {showLanding && (
        <LandingPage
onStartGame={() => {
            if (!isLoading && memoizedQuestions && !isInitializingRef.current) {
              setShowLanding(false);
              setGameStarted(true);
              // Start bgm2 when game scene is ready
              const gameScene = phaserGameRef.current?.scene.getScene('GameScene');
              if (gameScene && gameScene.bgm2) {
                gameScene.bgm2.play();
              }
            }
          }}
          isLoading={isLoading}
          error={error}
          scores={scores}
          questions={memoizedQuestions || []}
        />
      )}

      {gameStarted && !gameOver && (
        <>
          {/* HUD */}
          <div className="w-full max-w-[1000px] hud rounded-lg p-2 md:p-4 flex flex-col gap-2 md:gap-4">
            <div className="flex flex-col gap-2 md:gap-4">
              {/* Top row with score and level */}
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-1 md:gap-2">
                    {[...Array(Math.max(0, lives))].map((_, i) => (
                      <Heart
                        key={i}
                        className="w-5 h-5 md:w-8 md:h-8 text-red-500 animate-pulse"
                        fill="currentColor"
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-1 md:gap-2">
                    <Trophy className="w-5 h-5 md:w-8 md:h-8 text-yellow-500" />
                    <span className="text-lg md:text-2xl font-bold text-white">
                      {score}
                    </span>
                  </div>
                  {combo > 2 && (
                    <div className="flex items-center gap-1 md:gap-2">
                      <Star
                        className="w-5 h-5 md:w-8 md:h-8 text-yellow-400"
                        fill="currentColor"
                      />
                      <span className="text-lg md:text-2xl font-bold text-yellow-400">
                        x{combo}
                      </span>
                    </div>
                  )}
                </div>

                <div className="text-lg md:text-2xl font-bold text-white">
                  レベル {level}
                </div>
              </div>
            </div>
          </div>

          {/* Phaser Game Container */}
          {/* Game Layout Container */}
          <div className="flex flex-col w-full max-w-[1000px]">
            {/* Question Container - 10% height */}
            <div className="question-container">
              <div className="question-text text-white font-medium">
                {currentQuestion}
              </div>
            </div>

            {/* Game Container - 80% height */}
            <div
              id="phaser-container"
              className={`game-container rounded-lg overflow-hidden transition-opacity duration-500 ${
                isGameReady ? 'opacity-100' : 'opacity-0'
              }`}
            />
          </div>
        </>
      )}

      {gameOver && (
        <>
          <ScoreSubmissionDialog
            isOpen={showScoreSubmission}
            onClose={() => setShowScoreSubmission(false)}
            score={score}
            scores={scores}
            className={sheetName || 'visitor'} // Pass the class name
            onScoreSubmitted={async () => {
              try {
                const response = await fetchQuestions(sheetName);
                setScores(response.scores || []);
              } catch (error) {
                console.error('Error refreshing scores:', error);
              }
            }}
          />
          <div className="absolute inset-0 game-over flex flex-col items-center justify-center bg-black bg-opacity-70">
            <h2 className="text-5xl font-bold text-white mb-8 drop-shadow-glow">
              Game Over!
            </h2>
            <p className="text-6xl text-white mb-8">
              最終スコア：{' '}
              <span 
                className="text-yellow-400 inline-block text-8xl font-bold"
                style={{
                  textShadow: '0 0 20px rgba(250, 204, 21, 0.5)',
                  transition: 'transform 0.2s ease-out'
                }}
              >
                {displayScore}
              </span>
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  // First stop and destroy the current game instance
                  if (phaserGameRef.current) {
                    // Get the game scene and reset arrays
                    const gameScene = phaserGameRef.current.scene.getScene('GameScene');
                    if (gameScene) {
                      console.log('Resetting game arrays...');
                      gameScene.usedQuestions = [];
                      gameScene.questionResults = [];
                      console.log('Arrays reset. Used Questions:', gameScene.usedQuestions);
                    }

                    // Stop all current scenes
                    phaserGameRef.current.scene.scenes.forEach((scene) => {
                      scene.sound?.stopAll();
                      scene.events?.removeAllListeners();
                    });
                    phaserGameRef.current.destroy(true);
                    phaserGameRef.current = null;
                  }

                  // Reset all game states
                  setScore(0);
                  setLives(3);
                  setLevel(1);
                  setCombo(0);
                  setGameOver(false);

                  // Important: Set gameStarted to false first
                  setGameStarted(false);

                  // Then use setTimeout to ensure state updates before restarting
                  setTimeout(() => {
                    setGameStarted(true);
                  }, 0);
                }}
                className="px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-xl font-bold 
                        hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-105 
                        focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
              >
                もう一度プレイ
              </button>
              <button
                onClick={resetGame}
                className="px-8 py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg text-xl font-bold 
                        hover:from-gray-600 hover:to-gray-700 transition-all transform hover:scale-105 
                        focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
              >
                メニューに戻る
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GrammarGame;
